/**
 * @fileoverview Componente responsável por alternar o idioma da interface.
 */

import React, { useEffect, useState } from 'react';
import { changeLanguage, i18n } from "../../translate/i18n";
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import api from "../../services/api";

import { Check, Globe } from "lucide-react";
const languages = [
    { code: 'pt', flag: '🇧🇷', name: 'Português' },
    { code: 'en', flag: '🇺🇸', name: 'English' },
    { code: 'es', flag: '🇪🇸', name: 'Español' }
];

const useStyles = makeStyles(theme => ({
    '@keyframes fadeSlideIn': {
        '0%': {
            opacity: 0,
            transform: 'translateY(-8px)'
        },
        '100%': {
            opacity: 1,
            transform: 'translateY(0)'
        }
    },
    '@keyframes checkPop': {
        '0%': {
            opacity: 0,
            transform: 'scale(0.5)'
        },
        '50%': {
            transform: 'scale(1.2)'
        },
        '100%': {
            opacity: 1,
            transform: 'scale(1)'
        }
    },
    container: {
        padding: '16px',
        minWidth: '200px',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        marginBottom: '16px',
        paddingBottom: '12px',
        borderBottom: theme.palette.type === 'dark'
            ? '1px solid rgba(255, 255, 255, 0.08)'
            : '1px solid rgba(0, 0, 0, 0.06)',
    },
    headerIcon: {
        fontSize: '20px',
        color: theme.palette.type === 'dark'
            ? '#5b7cff'
            : '#5b7cff',
    },
    headerText: {
        fontSize: '14px',
        fontWeight: 600,
        color: theme.palette.type === 'dark'
            ? 'rgba(255, 255, 255, 0.9)'
            : 'rgba(0, 0, 0, 0.8)',
        letterSpacing: '0.3px',
    },
    languageList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
    languageCard: {
        display: 'flex',
        alignItems: 'center',
        padding: '12px 14px',
        borderRadius: '12px',
        cursor: 'pointer',
        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        border: theme.palette.type === 'dark'
            ? '1px solid rgba(255, 255, 255, 0.06)'
            : '1px solid rgba(0, 0, 0, 0.04)',
        backgroundColor: theme.palette.type === 'dark'
            ? 'rgba(255, 255, 255, 0.03)'
            : 'rgba(0, 0, 0, 0.01)',
        animation: '$fadeSlideIn 0.3s ease forwards',
        opacity: 0,
        '&:hover': {
            backgroundColor: theme.palette.type === 'dark'
                ? 'rgba(91, 124, 255, 0.08)'
                : 'rgba(91, 124, 255, 0.06)',
            borderColor: theme.palette.type === 'dark'
                ? 'rgba(91, 124, 255, 0.25)'
                : 'rgba(91, 124, 255, 0.2)',
            transform: 'translateX(4px)',
        },
        '&:active': {
            transform: 'translateX(4px) scale(0.98)',
        },
        '&:focus': {
            outline: 'none',
            borderColor: '#5b7cff',
            boxShadow: '0 0 0 2px rgba(91, 124, 255, 0.2)',
        },
    },
    selected: {
        backgroundColor: theme.palette.type === 'dark'
            ? 'rgba(91, 124, 255, 0.12)'
            : 'rgba(91, 124, 255, 0.08)',
        borderColor: theme.palette.type === 'dark'
            ? 'rgba(91, 124, 255, 0.4)'
            : 'rgba(91, 124, 255, 0.35)',
        boxShadow: theme.palette.type === 'dark'
            ? '0 2px 12px rgba(91, 124, 255, 0.15), inset 0 0 20px rgba(91, 124, 255, 0.05)'
            : '0 2px 8px rgba(91, 124, 255, 0.12)',
        '&:hover': {
            backgroundColor: theme.palette.type === 'dark'
                ? 'rgba(91, 124, 255, 0.15)'
                : 'rgba(91, 124, 255, 0.1)',
            borderColor: theme.palette.type === 'dark'
                ? 'rgba(91, 124, 255, 0.5)'
                : 'rgba(91, 124, 255, 0.4)',
        },
    },
    flag: {
        fontSize: '24px',
        lineHeight: 1,
        marginRight: '12px',
        filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))',
    },
    langName: {
        fontSize: '14px',
        fontWeight: 500,
        color: theme.palette.type === 'dark'
            ? 'rgba(255, 255, 255, 0.85)'
            : 'rgba(0, 0, 0, 0.75)',
        flex: 1,
        letterSpacing: '0.2px',
    },
    checkIcon: {
        fontSize: '18px',
        color: '#5b7cff',
        animation: '$checkPop 0.3s ease forwards',
        filter: theme.palette.type === 'dark'
            ? 'drop-shadow(0 0 4px rgba(91, 124, 255, 0.5))'
            : 'none',
    },
}));

const LanguageControl = ({ showHeader = true }) => {
    const classes = useStyles();
    const [selectedLanguage, setSelectedLanguage] = useState('pt');

    const handleLanguageChange = async (langCode) => {
        setSelectedLanguage(langCode);
        changeLanguage(langCode);

        try {
            await api.post(`/users/set-language/${langCode}`);
        } catch (error) {
            console.error(error);
        }
    };

    const handleKeyDown = (event, langCode) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleLanguageChange(langCode);
        }
    };

    useEffect(() => {
        const savedLanguage = localStorage.getItem('i18nextLng');
        if (savedLanguage) {
            setSelectedLanguage(savedLanguage);
        }
    }, []);

    return (
        <div className={classes.container}>
            {showHeader && (
                <div className={classes.header}>
                    <Globe className={classes.headerIcon} />
                    <span className={classes.headerText}>
                        {i18n.t("selectLanguage")}
                    </span>
                </div>
            )}
            <div className={classes.languageList} role="radiogroup" aria-label="Select language">
                {languages.map((lang, index) => (
                    <div
                        key={lang.code}
                        className={clsx(classes.languageCard, {
                            [classes.selected]: selectedLanguage === lang.code
                        })}
                        onClick={() => handleLanguageChange(lang.code)}
                        onKeyDown={(e) => handleKeyDown(e, lang.code)}
                        role="radio"
                        aria-checked={selectedLanguage === lang.code}
                        tabIndex={0}
                        style={{ animationDelay: `${index * 60}ms` }}
                    >
                        <span className={classes.flag} role="img" aria-label={lang.name}>
                            {lang.flag}
                        </span>
                        <span className={classes.langName}>{lang.name}</span>
                        {selectedLanguage === lang.code && (
                            <Check className={classes.checkIcon} />
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default LanguageControl;
